import { useEffect, useRef, useState } from "react";
import { Crown, Flame, HeartPulse, Skull } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { franchiseById } from "../data/riotballTeams";

const API = process.env.REACT_APP_BACKEND_URL;
/* game record keys → dossier ids (three older keys predate the dossier naming) */
const RECORD_TO_DOSSIER = { red: "red-force", white: "white-guards", vultures: "voltaic-vultures" };
const dossierId = (key) => RECORD_TO_DOSSIER[key] || key;

/* legends board — seats are pinned in official order; rank is computed by
   position. Pinned NET-REGISTERED seats (RebelXMegatron…) read their stash live
   from the vault the moment the account exists, and never double-list below. */
const PINNED_LIVE = new Set(["REBELXMEGATRON"]);

const runners = [
  { name: "DOLO", route: "ALL SECTORS — APEX COURIER MK-VII", status: "ACTIVE", deliveries: 2362875 },
  { name: "REBELXMEGATRON", route: "NET-REGISTERED — ALLIANCE CIRCUIT", status: "ACTIVE", deliveries: null, real: true },
  { name: "RIFF", route: "SECTOR 7 — STATIC LINE", status: "LEGEND", deliveries: 2940 },
  { name: "OPUS", route: "NEW ATLANTA — VINYL RUN", status: "LEGEND", deliveries: 2712 },
  { name: "GOJI", route: "DUST RIDGES — HEAVY HAUL", status: "ACTIVE", deliveries: 2455 },
  { name: "KOJI", route: "SWARM BORDER — NIGHT WATCH", status: "INJURED", deliveries: 2301 },
  { name: "LIL GIRL", route: "SAFE ZONE B — LOW PROFILE", status: "ACTIVE", deliveries: 1980 },
  { name: "DRONE BOY", route: "SECTOR 12 — AIR SHADOW", status: "ACTIVE", deliveries: 1742 },
  { name: "SABLE", route: "VINYL BLADE — ESCORT WING", status: "INJURED", deliveries: 1506 },
  { name: "CINDERJACK", route: "ASH FALLS — EMBER LINE", status: "ACTIVE", deliveries: 1188 },
  { name: "NULL-ROUTE", route: "SECTOR 00 — UNMAPPED", status: "LEGEND", deliveries: 964 },
];

const normName = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const statusMeta = {
  LEGEND: { icon: Crown, className: "roster-status-legend" },
  ACTIVE: { icon: HeartPulse, className: "roster-status-active" },
  INJURED: { icon: Skull, className: "roster-status-injured" },
};

export function RunnerRoster() {
  const scrollRef = useRef(null);
  const pausedRef = useRef(false);
  const [realRunners, setRealRunners] = useState([]);
  const [leagueLeader, setLeagueLeader] = useState(null);

  // the league race follows the roster — tiny live standings chip
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/riotball/records`)
      .then((r) => (r.ok ? r.json() : null))
      .then((records) => {
        if (!alive || !records) return;
        const entries = Object.entries(records).filter(([, best]) => best > 0);
        if (!entries.length) return;
        const [key, best] = entries.sort((a, b) => b[1] - a[1])[0];
        const team = franchiseById(dossierId(key));
        if (team) setLeagueLeader({ team, best });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  /* every vault registration joins the board automatically — the roster reads
     the live user list, and re-polls every minute so stashes stay accurate */
  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/roster`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (alive) setRealRunners(Array.isArray(data?.runners) ? data.runners : []);
        })
        .catch(() => {});
    load();
    const timer = window.setInterval(load, 60000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  const legends = runners.map((r, i) => ({ ...r, rank: i + 1 }));
  /* pinned seats read live vault data when the account exists */
  const liveByName = Object.fromEntries(realRunners.map((u) => [normName(u.name), u]));
  const legendsLive = legends.map((r) => {
    const hit = PINNED_LIVE.has(normName(r.name)) ? liveByName[normName(r.name)] : null;
    return hit ? { ...r, deliveries: hit.points, route: `NET-REGISTERED — ${hit.tag}`, franchise: hit.franchise || null, streak: hit.streak || false } : r;
  });
  const liveRows = realRunners
    .filter((u) => !PINNED_LIVE.has(normName(u.name)))
    .map((u, i) => ({
      rank: legendsLive.length + i + 1,
      name: u.name,
      route: `NET-REGISTERED — ${u.tag}`,
      status: "ACTIVE",
      deliveries: u.points,
      streak: u.streak || false,
      franchise: u.franchise || null,
      real: true,
    }));
  const allRunners = [...legendsLive, ...liveRows];

  const [tallies, setTallies] = useState(() => Object.fromEntries(legends.map((r) => [r.name, r.deliveries ?? 0])));

  useEffect(() => {
    setTallies((prev) => {
      const next = { ...prev };
      liveRows.forEach((r) => {
        next[r.name] = r.deliveries;
      });
      legendsLive.forEach((r) => {
        if (r.deliveries !== null) next[r.name] = r.deliveries;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realRunners]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    let raf;
    let last = 0;
    const step = (time) => {
      if (last && !pausedRef.current) {
        el.scrollTop += (time - last) * 0.03;
        const half = el.scrollHeight / 2;
        if (el.scrollTop >= half) el.scrollTop -= half;
      }
      last = time;
      raf = window.requestAnimationFrame(step);
    };
    raf = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let active = true;
    let timer;
    const tick = () => {
      if (!active) return;
      /* official seats hold their published numbers — the ticker only bumps
         unpinned lore runners, never Dolo's record or live registered stashes */
      const activeRunners = legends.filter((r) => r.status === "ACTIVE" && !r.real && r.name !== "DOLO");
      const pick = activeRunners[Math.floor(Math.random() * activeRunners.length)];
      setTallies((prev) => ({ ...prev, [pick.name]: prev[pick.name] + 1 }));
      timer = window.setTimeout(tick, 22000 + Math.random() * 26000);
    };
    timer = window.setTimeout(tick, 9000);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, []);

  const counts = allRunners.reduce(
    (acc, runner) => ({ ...acc, [runner.status]: (acc[runner.status] || 0) + 1 }),
    {},
  );

  const pause = () => {
    pausedRef.current = true;
  };
  const resume = () => {
    pausedRef.current = false;
  };
  const resumeDelayed = () => {
    window.setTimeout(resume, 1600);
  };

  return (
    <section className="runner-roster" data-testid="runner-roster-section">
      <Reveal className="roster-heading">
        <p className="eyebrow" data-testid="runner-roster-eyebrow">WASTELAND RUNNER ROSTER</p>
        <h2>2,362,875 deliveries. Nobody is close.</h2>
        <p>
          The Black-Ops Net tracks every registered runner crossing the wasteland. Dolo holds the all-time record —
          and every new vault registration joins this board automatically, stash PTS counted live.
        </p>
        {leagueLeader ? (
          <Link
            to="/riotball"
            className="roster-league-chip"
            style={{ "--team-color": leagueLeader.team.color }}
            data-testid="roster-league-chip"
          >
            <Crown size={12} />
            AL RIOTBALL LEAGUE — 1ST&nbsp;<strong>{leagueLeader.team.name}</strong>&nbsp;· {leagueLeader.best}
            <img src={`/assets/team-logo-${leagueLeader.team.id}.png`} alt="" loading="lazy" />
          </Link>
        ) : null}
      </Reveal>
      <Reveal className="roster-terminal" data-testid="roster-terminal">
        <div className="roster-stats">
          <span data-testid="roster-stat-legend">
            LEGENDS <strong>{String(counts.LEGEND || 0).padStart(2, "0")}</strong>
          </span>
          <span data-testid="roster-stat-active">
            RUNNERS ACTIVE <strong>{String(counts.ACTIVE || 0).padStart(2, "0")}</strong>
          </span>
          <span data-testid="roster-stat-injured">
            INJURED <strong>{String(counts.INJURED || 0).padStart(2, "0")}</strong>
          </span>
        </div>
        <div className="roster-head" aria-hidden="true">
          <span>RANK</span>
          <span>RUNNER / ROUTE</span>
          <span>STATUS</span>
          <span>DELIVERIES · PTS</span>
        </div>
        <div
          className="roster-scroll"
          data-testid="roster-scroll"
          ref={scrollRef}
          onBlur={resume}
          onFocus={pause}
          onMouseEnter={pause}
          onMouseLeave={resume}
          onTouchEnd={resumeDelayed}
          onTouchStart={pause}
        >
          {[...allRunners, ...allRunners].map((runner, index) => {
            const meta = statusMeta[runner.status];
            const StatusIcon = meta.icon;
            return (
              <div
                className={`roster-row${runner.rank === 1 ? " roster-row-first" : ""}${runner.real ? " roster-row-real" : ""}`}
                data-testid={index < allRunners.length ? (runner.real ? `roster-row-real-${runner.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : `roster-row-rank-${runner.rank}`) : undefined}
                key={`${runner.name}-${index}`}
              >
                <span className="roster-rank">{String(runner.rank).padStart(2, "0")}</span>
                <span className="roster-runner">
                  <strong>
                    {runner.name}
                    {runner.streak ? (
                      <Flame size={12} className="roster-streak-flame" data-testid={`streak-flame-${runner.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} aria-label="Daily streak badge" />
                    ) : null}
                  </strong>
                  <small>{runner.route}</small>
                  {runner.franchise && franchiseById(runner.franchise) && (
                    <Link
                      to={`/riotball/team/${runner.franchise}`}
                      className="roster-franchise"
                      style={{ "--team-color": franchiseById(runner.franchise).color }}
                      data-testid={index < allRunners.length ? `roster-franchise-${runner.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}
                      onClick={pause}
                    >
                      <img src={`/assets/team-logo-${runner.franchise}.png`} alt="" loading="lazy" />
                      {franchiseById(runner.franchise).name}
                    </Link>
                  )}
                </span>
                <span className={`roster-status ${meta.className}`}>
                  <StatusIcon size={11} />
                  {runner.status}
                </span>
                <span
                  className="roster-deliveries"
                  data-testid={index < allRunners.length ? `roster-deliveries-${runner.rank}` : undefined}
                >
                  <strong key={tallies[runner.name] ?? runner.deliveries ?? "tbc"}>
                    {runner.deliveries === null ? "—" : (tallies[runner.name] ?? runner.deliveries).toLocaleString()}
                  </strong>
                </span>
              </div>
            );
          })}
        </div>
      </Reveal>
    </section>
  );
}
